import { Loader, Stack, TextInput } from "@mantine/core"
import { IconSearch } from "@tabler/icons-react"
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table"
import { SetStateAction, Dispatch, useState, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { AdminResponses } from "@tfg/types/endpoints/admin"
import { translationFiltersSchema } from "@tfg/types/schemas/admin"

import { useSearchParamsFilters } from "~/components/hooks/useSearchParamsFilters"
import { Table } from "~/components/table/Table"

import { useColumns } from "./columns"

type TranslationsAdminProps = {
  components: AdminResponses.Translation[]
  isFetching: boolean
  pagination: { pageIndex: number; pageSize: number }
  setPagination: Dispatch<
    SetStateAction<{
      pageIndex: number
      pageSize: number
    }>
  >
  translationsTotal: number
}

export function TranslationsAdmin({
  components,
  pagination,
  isFetching,
  setPagination,
  translationsTotal,
}: TranslationsAdminProps) {
  const { t } = useTranslation("admin")
  const [searchParams, setSearchParams] = useSearchParamsFilters(
    translationFiltersSchema
  )
  const columns = useColumns()
  const [sorting, setSorting] = useState<SortingState>([
    { id: "originalText", desc: false },
  ])
  const pageCount = useMemo(
    () => Math.ceil(translationsTotal / pagination.pageSize),
    [translationsTotal, pagination.pageSize]
  )

  const table = useReactTable({
    data: components,
    columns,
    state: {
      pagination,
      sorting,
    },
    pageCount,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onGlobalFilterChange: (value) =>
      setSearchParams((prev: any) => ({ ...prev, search: value })),
    manualPagination: true,
  })

  return (
    <Stack gap="xs">
      <TextInput
        label={t("translations.search.label")}
        value={searchParams.search ?? ""}
        autoFocus
        onChange={(e) => setSearchParams({ search: e.target.value })}
        w={180}
        size="xs"
        leftSection={<IconSearch size={16} />}
        rightSection={isFetching && <Loader size={16} />}
      />
      <Table
        noColumnWrap={false}
        table={table}
        pageIndex={pagination.pageIndex}
        manualPaginationCount={components.length}
      />
    </Stack>
  )
}
